import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FiX, FiSmartphone, FiDownload } from 'react-icons/fi';

const PWAInstallModal = ({ isOpen, onClose, onInstall, canInstall }) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const isAndroid = /Android/i.test(navigator.userAgent);

  return (
    <Overlay onClick={onClose}>
      <Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-install-title"
        onClick={(event) => event.stopPropagation()}
      >
        <CloseButton onClick={onClose} aria-label="Close">
          <FiX />
        </CloseButton>

        <IconBox>
          <FiSmartphone />
        </IconBox>

        <Eyebrow>YODA ON THE GO</Eyebrow>
        <h2 id="pwa-install-title">Install YODA Safety Services</h2>

        <Description>
          Add YODA to your device for quick access to your safety training,
          courses, and certificates.
        </Description>

        {canInstall ? (
          <PrimaryButton onClick={onInstall}>
            <FiDownload />
            Install YODA
          </PrimaryButton>
        ) : isIOS ? (
          <Instructions>
            <h3>Install on iPhone or iPad</h3>
            <p>1. Open YODA in Safari.</p>
            <p>2. Tap the Share button.</p>
            <p>3. Select Add to Home Screen.</p>
            <p>4. Tap Add to finish.</p>
          </Instructions>
        ) : isAndroid ? (
          <Instructions>
            <h3>Install on Android</h3>
            <p>1. Open YODA in Chrome.</p>
            <p>2. Tap the browser menu (⋮).</p>
            <p>3. Select Install app or Add to Home screen.</p>
            <p>4. Confirm the installation.</p>
          </Instructions>
        ) : (
          <Instructions>
            <h3>Install on your computer</h3>
            <p>1. Open YODA in Chrome or Microsoft Edge.</p>
            <p>2. Open the browser menu.</p>
            <p>3. Select Install YODA Safety Services.</p>
            <p>4. Confirm the installation.</p>
          </Instructions>
        )}

        <Note>
          Installation is free. Your existing YODA account works in the
          installed application.
        </Note>
      </Modal>
    </Overlay>
  );
};

PWAInstallModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInstall: PropTypes.func.isRequired,
  canInstall: PropTypes.bool.isRequired,
};

export default PWAInstallModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Modal = styled.div`
  position: relative;
  width: min(100%, 480px);
  max-height: 90dvh;
  overflow-y: auto;
  background: #fff;
  color: #171717;
  border-radius: 18px;
  padding: 38px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);

  h2 {
    margin: 10px 0 14px;
    font-size: clamp(1.5rem, 4vw, 2rem);
    line-height: 1.2;
  }

  @media (max-width: 600px) {
    padding: 28px 22px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  border: none;
  background: #f3f3f3;
  color: #171717;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: grid;
  place-items: center;
`;

const IconBox = styled.div`
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: #fff4ce;
  color: #171717;
  font-size: 27px;
`;

const Eyebrow = styled.span`
  display: block;
  margin-top: 20px;
  color: #9a6900;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.14em;
`;

const Description = styled.p`
  color: #555;
  line-height: 1.75;
`;

const Instructions = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: #f7f7f7;
  border-radius: 12px;

  h3 {
    margin: 0 0 14px;
    font-size: 1rem;
  }

  p {
    margin: 8px 0;
    color: #555;
    line-height: 1.6;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 24px;
  padding: 15px 20px;
  background: var(--yellow4, #f4c542);
  color: #111;
  border: none;
  border-radius: 10px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    filter: brightness(0.95);
  }
`;

const Note = styled.p`
  margin: 24px 0 0;
  font-size: 0.8rem;
  line-height: 1.6;
  color: #777;
`;
